const DEFAULT_MAX_EDGE = 1024;
const DEFAULT_QUALITY = 0.9;

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
    img.src = dataUrl;
  });
}

function stripDataUrl(dataUrl) {
  return dataUrl.split(",")[1]; // Remove the "data:image/...;base64," part
}

// Reads a file and returns { base64String, fileExtension }. If the file is an
// image larger than maxEdge on its longest side, it is downscaled in the browser
// before encoding so we never ship a 20+ MB original just to make a small thumbnail
// (the API re-encodes to a 250x250 WebP anyway, and Kestrel caps the body at 30 MB).
export async function getBase64AndExtension(file, maxEdge = DEFAULT_MAX_EDGE, quality = DEFAULT_QUALITY) {
  if (!file) {
    throw new Error("No file provided");
  }

  const dataUrl = await readAsDataUrl(file);
  const originalExtension = file.name.split(".").pop();

  // Not an image (or a type the browser can't decode) — return the raw bytes untouched.
  if (!file.type || !file.type.startsWith("image/")) {
    return { base64String: stripDataUrl(dataUrl), fileExtension: originalExtension };
  }

  let img;
  try {
    img = await loadImage(dataUrl);
  } catch {
    return { base64String: stripDataUrl(dataUrl), fileExtension: originalExtension };
  }

  const longestEdge = Math.max(img.width, img.height);
  const scale = longestEdge > maxEdge ? maxEdge / longestEdge : 1;

  // Already small enough — keep the original bytes/format, skip the canvas round-trip.
  if (scale === 1) {
    return { base64String: stripDataUrl(dataUrl), fileExtension: originalExtension };
  }

  const targetWidth = Math.round(img.width * scale);
  const targetHeight = Math.round(img.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  canvas.getContext("2d").drawImage(img, 0, 0, targetWidth, targetHeight);

  // Preserve PNG (keeps transparency); everything else becomes JPEG to keep the
  // payload tiny. Extension only needs to be a non-empty valid image hint server-side.
  const outputType = file.type === "image/png" ? "image/png" : "image/jpeg";
  const fileExtension = outputType === "image/png" ? "png" : "jpg";
  const resizedDataUrl = canvas.toDataURL(outputType, quality);

  return { base64String: stripDataUrl(resizedDataUrl), fileExtension };
}
